/**
 * Manage Page - Settings Hub with URL-based routing
 *
 * Routes:
 * - /manage - Dashboard
 * - /manage/training - Face recognition (all roles)
 * - /manage/teams - Team CRUD with aliases (MANAGER, PLANNER)
 * - /manage/persons - Person CRUD, aliases, merge (MANAGER, PLANNER)
 * - /manage/unlinked - Persons without team assignment (MANAGER, PLANNER)
 * - /manage/company - Company hub (MANAGER only)
 * - /manage/company/users - User management
 * - /manage/company/ftp-targets - FTP output targets
 * - /manage/company/profiles - Processing profiles
 * - /manage/company/settings - Company settings
 * - /manage/company/caption-builder - Caption builder config
 */

import { useState, useCallback, useEffect } from 'react';
import { usePermissions } from '../hooks';
import Training from './Training';
import {
  ManageNav,
  ManageDashboard,
  TeamList,
  PersonList,
  MergePersonsModal,
  CompanyDashboard,
  UserList,
  FtpTargetList,
  ProfileList,
  CompanySettings,
  CaptionBuilderSettings,
  UnlinkedPlayers,
} from '../features/manage/components';
import styles from './Manage.module.css';

export default function Manage({ onNavigate }) {
  const { canManagePersons, canManageTeams, canManageCompany } = usePermissions();

  // Get current path from window.location and hash
  const getPathInfo = () => {
    const pathname = window.location.pathname;
    const hash = window.location.hash.replace('#', '');

    // Support both URL-based (/manage/teams) and hash-based (/manage#teams) routing
    if (pathname.startsWith('/manage/')) {
      const segments = pathname.replace('/manage/', '').split('/').filter(Boolean);
      return { section: segments[0] || '', subSection: segments[1] || '' };
    }

    // Hash-based routing for backwards compatibility
    if (hash) {
      const segments = hash.split('/').filter(Boolean);
      return { section: segments[0] || '', subSection: segments[1] || '' };
    }

    return { section: '', subSection: '' };
  };

  const [pathInfo, setPathInfo] = useState(getPathInfo);

  // Listen for hash changes
  useEffect(() => {
    const handler = () => setPathInfo(getPathInfo());
    window.addEventListener('hashchange', handler);
    window.addEventListener('popstate', handler);
    return () => {
      window.removeEventListener('hashchange', handler);
      window.removeEventListener('popstate', handler);
    };
  }, []);

  const currentSection = pathInfo.section;
  const currentSubSection = pathInfo.subSection;
  const subPath = currentSubSection ? '/' + currentSubSection : '';
  const path = currentSection ? `/manage/${currentSection}${subPath}` : '/manage';

  // Merge modal state
  const [mergePersons, setMergePersons] = useState(null);

  // Handle merge action from PersonList
  const handleMerge = useCallback((persons) => {
    setMergePersons(persons);
  }, []);

  const handleMergeSuccess = useCallback(() => {
    setMergePersons(null);
    // PersonList will refetch on its own
  }, []);

  // Render content based on route
  const renderContent = () => {
    // Company sub-routes
    if (currentSection === 'company') {
      if (!canManageCompany) {
        return <AccessDenied />;
      }

      switch (currentSubSection) {
        case 'users':
          return <UserList />;
        case 'ftp-targets':
          return <FtpTargetList />;
        case 'profiles':
          return <ProfileList />;
        case 'settings':
          return <CompanySettings />;
        case 'caption-builder':
          return <CaptionBuilderSettings />;
        default:
          return <CompanyDashboard />;
      }
    }

    // Top-level routes
    switch (currentSection) {
      case 'training':
        return <Training embedded />;
      case 'teams':
        if (!canManageTeams) return <AccessDenied />;
        return <TeamList />;
      case 'persons':
        if (!canManagePersons) return <AccessDenied />;
        return <PersonList onMerge={handleMerge} />;
      case 'unlinked':
        if (!canManagePersons) return <AccessDenied />;
        return <UnlinkedPlayers />;
      default:
        return <ManageDashboard />;
    }
  };

  return (
    <div className={styles.page}>
      <ManageNav
        currentPath={path}
        canManagePersons={canManagePersons}
        canManageTeams={canManageTeams}
        canManageCompany={canManageCompany}
      />
      <main className={styles.content}>
        {renderContent()}
      </main>

      {/* Merge Persons Modal */}
      {mergePersons?.length > 1 && (
        <MergePersonsModal
          persons={mergePersons}
          onClose={() => setMergePersons(null)}
          onSuccess={handleMergeSuccess}
        />
      )}
    </div>
  );
}

function AccessDenied() {
  return (
    <div className={styles.accessDenied}>
      <h2>Access Denied</h2>
      <p>You don&apos;t have permission to view this section.</p>
    </div>
  );
}
